import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { getCurrentDate } from "../../../configs/helpers";
import {
    fetchedRoute,
    fetchedVehiclesBySubsidiary,
    registerClientData,
    updateClientData,
} from "../../../apis/apis.dispatches/apisDispatch";
import { fetchedGangs } from "../../../apis/apis.logistics/apisProduct";
import { ContextAuth } from "../../../contexts/ContextAuth";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

const initialClientState = {
    personID: 0, name: "", firstSurname: "", secondSurname: "",
    documentType: "", documentNumber: "", observation: "", cellphone: "",
    visitDay: 0, customerType: 0, showcases: 0, physicalDistribution: "01",
    gangID: 0, district: "", address: "", latitude: 0, longitude: 0,
    comment: "", isEnabled: true,
};

const containerStyle = { width: "100%", height: "600px" };

const DAY_COLORS = ["#dc3545","#007bff","#4cd964","#ffc107","#6c757d","#6610f2","#343a40"];
const DAYS = [
    { id: 0, name: "LUNES" }, { id: 1, name: "MARTES" }, { id: 2, name: "MIERCOLES" },
    { id: 3, name: "JUEVES" }, { id: 4, name: "VIERNES" }, { id: 5, name: "SABADO" },
    { id: 6, name: "DOMINGO" },
];

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";
const thCls    = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300";
const tdCls    = "px-3 py-2 text-xs border-b border-gray-100 dark:border-gray-700 align-middle";

export function MapPage() {
    const [vehicles, setVehicles]               = useState([]);
    const [gangs, setGangs]                     = useState([]);
    const { auth }                              = useContext(ContextAuth);
    const [orders, setOrders]                   = useState([]);
    const [client, setClient]                   = useState(initialClientState);
    const [googleMapsApiLoaded, setGoogleMapsApiLoaded] = useState(false);
    const [mapCenter, setMapCenter]             = useState({ lat: -16.430613, lng: -71.532155 });
    const [mapZoom, setMapZoom]                 = useState(12);
    const [mapRef, setMapRef]                   = useState(null);
    const [isModalOpen, setIsModalOpen]         = useState(false);
    const [preservedMapState, setPreservedMapState] = useState(null);

    const [questionState, setQuestionState] = useState({
        startDate:       getCurrentDate("-"),
        subsidiaryID:    auth.data.subsidiary.subsidiary_id,
        gangID:          0,
        userID:          0,
        visitDay:        0,
        operationStatus: "02",
        isEnabled:       true,
        useDotTypeMarker: true,
    });

    // ── API helpers ──────────────────────────────────────────────────────────
    const getVehicles = async (id) => {
        try { setVehicles(await fetchedVehiclesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };
    const getGangs = async () => {
        try { setGangs(await fetchedGangs()); }
        catch (e) { throw e; }
    };
    const sendFilterDispatches = async (data) => {
        try {
            const response = await fetchedRoute(data);
            setOrders(response);
            updateMapViewIfNeeded(response);
            return response;
        } catch (e) { throw e; }
    };

    // ── Map helpers ──────────────────────────────────────────────────────────
    const preserveMapView = () => {
        if (mapRef) {
            const center = mapRef.getCenter();
            const zoom   = mapRef.getZoom();
            const state  = { lat: center.lat(), lng: center.lng(), zoom };
            setMapCenter(state); setMapZoom(zoom); setPreservedMapState(state);
        }
    };
    const restoreMapView = () => {
        if (preservedMapState && mapRef) {
            mapRef.setCenter({ lat: preservedMapState.lat, lng: preservedMapState.lng });
            mapRef.setZoom(preservedMapState.zoom);
            setMapCenter({ lat: preservedMapState.lat, lng: preservedMapState.lng });
            setMapZoom(preservedMapState.zoom);
        }
    };
    const updateMapViewIfNeeded = (newOrders) => {
        if (orders.length === 0 && newOrders.length > 0) {
            const centerLat = newOrders.reduce((s, o) => s + o.latitude, 0) / newOrders.length;
            const centerLng = newOrders.reduce((s, o) => s + o.longitude, 0) / newOrders.length;
            setMapCenter({ lat: centerLat, lng: centerLng });
        }
    };
    const updateMarkerPosition = (personID, newLat, newLng) => {
        setOrders((prev) => prev.map((o) => o.personID === personID ? { ...o, latitude: newLat, longitude: newLng } : o));
    };

    // ── Search ───────────────────────────────────────────────────────────────
    function searchSuggestions() {
        sendFilterDispatches(questionState).then((res) => {
            const n = res.length;
            toast.info(n > 1 ? `Se encontraron ${n} resultados.` : `Se encontró ${n} resultado(s).`);
        });
    }

    // ── PDF / Image download ─────────────────────────────────────────────────
    const loadPDFLibraries = () => {
        if (!window.html2canvas) {
            const s1 = document.createElement("script");
            s1.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            s1.onload = () => toast.success("Librerías PDF cargadas correctamente");
            s1.onerror = () => toast.error("Error cargando librerías PDF");
            document.head.appendChild(s1);
        }
        if (!window.jsPDF) {
            const s2 = document.createElement("script");
            s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
            s2.onerror = () => toast.error("Error cargando librerías PDF");
            document.head.appendChild(s2);
        }
    };

    const downloadMapAsPDF = async () => {
        try {
            toast.info("Generando PDF del mapa...");
            const mapContainer = document.querySelector(".map-container");
            if (!mapContainer) { toast.error("No se pudo encontrar el contenedor del mapa"); return; }
            if (!window.html2canvas) { toast.warning("Cargando librerías, intente nuevamente en unos segundos..."); return; }
            await new Promise((r) => setTimeout(r, 1000));
            const canvas = await window.html2canvas(mapContainer, { useCORS: true, allowTaint: true, scale: 2, backgroundColor: "#ffffff", logging: false });
            const imageData = canvas.toDataURL("image/png");
            if (window.jsPDF) {
                const { jsPDF } = window.jsPDF;
                const pdf = new jsPDF("landscape", "mm", "a4");
                pdf.setFontSize(16); pdf.text("Mapa de Ubicación de Clientes", 20, 20);
                pdf.setFontSize(10); pdf.text(`Generado el: ${new Date().toLocaleDateString("es-ES")}`, 20, 30);
                pdf.addImage(imageData, "PNG", 20, 40, 250, 150);
                pdf.setFontSize(8); pdf.text(`Total de clientes: ${orders.length}`, 20, 200);
                pdf.save(`Mapa_Clientes_${new Date().toISOString().split("T")[0]}.pdf`);
                toast.success("PDF generado exitosamente");
            } else {
                const link = document.createElement("a");
                link.download = `Mapa_Clientes_${new Date().toISOString().split("T")[0]}.png`;
                link.href = imageData; link.click();
                toast.success("Imagen del mapa descargada");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error al generar PDF del mapa.");
        }
    };

    // ── Map double-click: add new client ─────────────────────────────────────
    const handleMapDoubleClick = (event) => {
        setClient({ ...initialClientState, latitude: event.latLng.lat(), longitude: event.latLng.lng() });
        setOrders([...orders, { ...initialClientState, latitude: event.latLng.lat(), longitude: event.latLng.lng() }]);
        setIsModalOpen(true);
    };

    // ── Open modal from table row ─────────────────────────────────────────────
    const openClientModal = (item) => {
        setClient({
            personID: item.personID, name: item.name, firstSurname: item.firstSurname,
            secondSurname: item.secondSurname, documentType: item.documentType,
            documentNumber: item.documentNumber, observation: item.observation,
            cellphone: item.cellphone, visitDay: item.visitDay, customerType: item.customerType,
            showcases: item.showcases, physicalDistribution: item.physicalDistribution,
            gangID: item.gangID, district: item.district, address: item.address,
            latitude: item.latitude, longitude: item.longitude, comment: item.comment,
            isEnabled: item.isEnabled,
        });
        setIsModalOpen(true);
    };

    // ── Save client ───────────────────────────────────────────────────────────
    const handleSaveClient = async () => {
        try {
            await updateClientData(client);
            updateMarkerPosition(client.personID, client.latitude, client.longitude);
            setClient(initialClientState);
            toast.success("Cliente guardado con éxito!");
            setIsModalOpen(false);
            setTimeout(() => restoreMapView(), 100);
        } catch (e) { toast.error("Error al guardar el cliente"); }
    };

    // ── Effects ───────────────────────────────────────────────────────────────
    useEffect(() => {
        getVehicles(auth.data.subsidiary.subsidiary_id);
        getGangs();
        loadPDFLibraries();
    }, []);

    useEffect(() => {
        if (Number(questionState.gangID) > 0 && Number(questionState.visitDay) >= 0)
            searchSuggestions();
    }, [questionState.gangID, questionState.visitDay, questionState.isEnabled]);

    useEffect(() => {
        document.body.style.overflow = isModalOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isModalOpen]);

    // ── Marker icon builder ───────────────────────────────────────────────────
    const getMarkerIcon = (loc) => {
        if (!googleMapsApiLoaded) return null;
        if (questionState.useDotTypeMarker) {
            return {
                path: "M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0",
                fillColor: DAY_COLORS[loc.visitDay] ?? "#343a40",
                fillOpacity: 1.0,
                labelOrigin: { x: 10, y: -20 },
                strokeWeight: 1, scale: 0.15, rotation: 30,
            };
        }
        const pinMap = { "01": "pin-blue", "02": "pin-green", "03": "pin-red", "04": "pin-yellow", "05": "pin-sky-blue" };
        return {
            url: `/markers/${pinMap[loc.routeStatus] ?? "pin-green-light"}.png`,
            scaledSize: new window.google.maps.Size(45, 45),
        };
    };

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Ubicación de clientes
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Distribuciones</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Mapa</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4 space-y-4">

                    {/* Filter controls */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-map-marked-alt text-blue-500"></i>
                                Filtros del mapa
                            </h2>
                        </div>
                        <div className="px-5 py-4">
                            <div className="flex flex-wrap items-end gap-4">

                                {/* Cuadrilla */}
                                <div className="w-full sm:w-56">
                                    <label className={labelCls}><i className="fas fa-route mr-1"></i>Cuadrilla</label>
                                    <select
                                        name="gangID"
                                        value={questionState.gangID}
                                        onChange={(e) => setQuestionState({ ...questionState, gangID: e.target.value })}
                                        className={inputCls}
                                    >
                                        <option value={0}>Elegir...</option>
                                        {vehicles.map((item, i) => (
                                            <option key={i} value={item.gang.gangID}>
                                                {item.vehicleLicensePlate} | {item.gang.gangName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Día */}
                                <div className="w-full sm:w-40">
                                    <label className={`${labelCls} text-orange-600 dark:text-orange-400`}><i className="fas fa-calendar-day mr-1"></i>Día</label>
                                    <select
                                        name="visitDay"
                                        value={questionState.visitDay}
                                        onChange={(e) => setQuestionState({ ...questionState, visitDay: e.target.value })}
                                        className={`${inputCls} text-orange-700 dark:text-orange-300 font-medium`}
                                    >
                                        {DAYS.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                        <option value={7}>TODOS</option>
                                    </select>
                                </div>

                                {/* Toggle: Activos / Dados de baja */}
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={questionState.isEnabled}
                                        onClick={() => setQuestionState({ ...questionState, isEnabled: !questionState.isEnabled })}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${questionState.isEnabled ? "bg-emerald-600" : "bg-gray-300 dark:bg-gray-600"}`}
                                    >
                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${questionState.isEnabled ? "translate-x-5" : "translate-x-0"}`} />
                                    </button>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {questionState.isEnabled ? "Activos" : "Dados de baja"}
                                    </span>
                                </div>

                                {/* Toggle: Tipo de marcador */}
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={questionState.useDotTypeMarker}
                                        onClick={() => setQuestionState({ ...questionState, useDotTypeMarker: !questionState.useDotTypeMarker })}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${questionState.useDotTypeMarker ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
                                    >
                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${questionState.useDotTypeMarker ? "translate-x-5" : "translate-x-0"}`} />
                                    </button>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {questionState.useDotTypeMarker ? "Marcadores punto" : "Marcadores app"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Map + table */}
                    {orders.length > 0 && (
                        <div className="space-y-4">

                            {/* Google Map */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                                <div className="map-container" style={{ position: "relative" }}>
                                    <LoadScript
                                        googleMapsApiKey="AIzaSyAEcm1ZrU9ssN2-00cCzUHw6jI7iTVRra8"
                                        onLoad={() => setGoogleMapsApiLoaded(true)}
                                    >
                                        <GoogleMap
                                            mapContainerStyle={containerStyle}
                                            center={mapCenter}
                                            zoom={mapZoom}
                                            onLoad={(map) => setMapRef(map)}
                                            onDblClick={handleMapDoubleClick}
                                            onDragEnd={preserveMapView}
                                            onZoomChanged={preserveMapView}
                                        >
                                            {orders.map((loc, i) => (
                                                <Marker
                                                    key={i}
                                                    position={{ lat: loc.latitude, lng: loc.longitude }}
                                                    label={questionState.useDotTypeMarker
                                                        ? { text: `\0\r\0\r\0\r\0\r\0\r${loc.observation}`, fontSize: "15px", fontWeight: "bold", color: "#343a40" }
                                                        : { text: `${loc.observation}`, fontSize: "15px", fontWeight: "normal", color: "#fff" }
                                                    }
                                                    title={loc.observation}
                                                    icon={getMarkerIcon(loc)}
                                                    draggable
                                                    onDragEnd={(marker) => {
                                                        preserveMapView();
                                                        setIsModalOpen(true);
                                                        updateMarkerPosition(loc.personID, marker.latLng.lat(), marker.latLng.lng());
                                                        setTimeout(() => {
                                                            setClient({
                                                                personID: loc.personID, name: loc.name, firstSurname: loc.firstSurname,
                                                                secondSurname: loc.secondSurname, documentType: loc.documentType,
                                                                documentNumber: loc.documentNumber, observation: loc.observation,
                                                                cellphone: loc.cellphone, visitDay: loc.visitDay, customerType: loc.customerType,
                                                                showcases: loc.showcases, physicalDistribution: loc.physicalDistribution,
                                                                gangID: loc.gangID, district: loc.district, address: loc.address,
                                                                latitude: marker.latLng.lat(), longitude: marker.latLng.lng(),
                                                                comment: loc.comment, isEnabled: loc.isEnabled,
                                                            });
                                                        }, 100);
                                                    }}
                                                />
                                            ))}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>

                                {/* Day legend */}
                                <div className="flex flex-wrap gap-2 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                                    {DAYS.map((day, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-medium text-white"
                                            style={{ backgroundColor: DAY_COLORS[i] }}
                                        >
                                            {day.name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Clients table */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                    <div>
                                        <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                            <i className="fas fa-users text-blue-500"></i>
                                            Lista de clientes
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{orders.length} clientes</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ReactHTMLTableToExcel
                                            id="test-table-xls-button"
                                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
                                            table="table-to-xls"
                                            filename="report_of_routes"
                                            sheet="report"
                                            buttonText="↓ Excel"
                                        />
                                        <button
                                            type="button"
                                            onClick={downloadMapAsPDF}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium px-3 py-1.5 transition-colors"
                                        >
                                            <i className="fas fa-file-pdf"></i>
                                            PDF Mapa
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { preserveMapView(); searchSuggestions(); }}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300 dark:border-cyan-700 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 text-xs font-medium px-3 py-1.5 transition-colors"
                                        >
                                            <i className="fas fa-sync-alt"></i>
                                            Recargar
                                        </button>
                                    </div>
                                </div>

                                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900" id="all-clients">
                                    <table className="min-w-full text-sm border-separate border-spacing-0" id="table-to-xls">
                                        <thead className="sticky top-0 z-10">
                                            <tr>
                                                {["Cód.","Cliente","Dirección","Documento","Exhibidor","Celular","Cuadrilla","Visita","Tipo Cliente","Lat","Lon","Estado Ruta","Acciones"].map((h) => (
                                                    <th key={h} className={thCls}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders
                                                .sort((a, b) => Number(a.observation) > Number(b.observation) ? 1 : -1)
                                                .map((item, index) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                        <td className={`${tdCls} text-center font-bold text-gray-700 dark:text-gray-300`}>{item.observation}</td>
                                                        <td className={`${tdCls} font-medium text-gray-800 dark:text-gray-200`}>{item.fullName}</td>
                                                        <td className={`${tdCls} text-gray-600 dark:text-gray-400`}>{item.address}</td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.documentNumber}</td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.showcasesDisplay}</td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.cellphone}</td>
                                                        <td className={`${tdCls} text-center`}>
                                                            <span className="inline-block rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-medium px-2 py-0.5">{item.gangName}</span>
                                                        </td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.visitDayDisplay}</td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.customerTypeDisplay}</td>
                                                        <td className={`${tdCls} text-center font-mono text-xs text-gray-500 dark:text-gray-400`}>{item.latitude}</td>
                                                        <td className={`${tdCls} text-center font-mono text-xs text-gray-500 dark:text-gray-400`}>{item.longitude}</td>
                                                        <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.routeStatusDisplay}</td>
                                                        <td className={`${tdCls} text-center`}>
                                                            <button
                                                                type="button"
                                                                onClick={() => openClientModal(item)}
                                                                className="inline-flex items-center gap-1 rounded-md border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-medium px-2.5 py-1 transition-colors"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                                Editar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Client modal — React controlled */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" aria-modal="true" role="dialog">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
                    <div className="relative z-10 w-full max-w-3xl flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden mb-10">

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 bg-blue-600 dark:bg-blue-700 flex-shrink-0">
                            <h5 className="text-base font-semibold text-white flex items-center gap-2">
                                <i className="fas fa-user-edit"></i>
                                Datos del cliente
                            </h5>
                            <button type="button" onClick={() => setIsModalOpen(false)} className="text-white/80 hover:text-white transition-colors p-1 rounded" aria-label="Cerrar">
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 bg-white dark:bg-gray-800">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                                {/* Nombre */}
                                <div className="sm:col-span-2">
                                    <label className={labelCls}>Nombre o razón social</label>
                                    <input type="text" maxLength={200} value={client.name || ""} onChange={(e) => setClient({ ...client, name: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Apellido paterno</label>
                                    <input type="text" maxLength={200} value={client.firstSurname || ""} onChange={(e) => setClient({ ...client, firstSurname: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Apellido materno</label>
                                    <input type="text" maxLength={200} value={client.secondSurname || ""} onChange={(e) => setClient({ ...client, secondSurname: e.target.value })} className={inputCls} />
                                </div>

                                <div className="sm:col-span-2 border-t border-gray-200 dark:border-gray-700 pt-4 mt-1"></div>

                                <div>
                                    <label className={labelCls}>Tipo de documento</label>
                                    <select value={client.documentType} onChange={(e) => setClient({ ...client, documentType: e.target.value })} className={inputCls}>
                                        <option value="">Elige</option>
                                        <option value="01">DNI</option>
                                        <option value="06">RUC</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Número de documento</label>
                                    <input type="text" maxLength={12} value={client.documentNumber || ""} onChange={(e) => setClient({ ...client, documentNumber: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Código</label>
                                    <input type="text" maxLength={20} value={client.observation || ""} onChange={(e) => setClient({ ...client, observation: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Celular</label>
                                    <input type="text" maxLength={12} value={client.cellphone || ""} onChange={(e) => setClient({ ...client, cellphone: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Tipo de cliente</label>
                                    <select value={client.customerType} onChange={(e) => setClient({ ...client, customerType: e.target.value })} className={inputCls}>
                                        <option value={0}>PEPSICO</option>
                                        <option value={1}>PROPIO</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Sis. de exhibición</label>
                                    <select value={client.showcases} onChange={(e) => setClient({ ...client, showcases: e.target.value })} className={inputCls}>
                                        <option value={0}>NO CUENTA</option>
                                        <option value={1}>AÉREO</option>
                                        <option value={2}>2 NIVELES</option>
                                        <option value={3}>5 NIVELES</option>
                                        <option value={4}>6X8 NIVELES</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Tipo de entrega</label>
                                    <select value={client.physicalDistribution} onChange={(e) => setClient({ ...client, physicalDistribution: e.target.value })} className={inputCls}>
                                        <option value="NA">NO APLICA</option>
                                        <option value="01">TIRAS</option>
                                        <option value="02">SUELTAS</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Ruta</label>
                                    <select value={client.gangID} onChange={(e) => setClient({ ...client, gangID: e.target.value })} className={inputCls}>
                                        <option value={0}>Elegir</option>
                                        {gangs.map((item, i) => <option key={i} value={item.gangID}>{item.gangName}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Día de visita</label>
                                    <select value={client.visitDay} onChange={(e) => setClient({ ...client, visitDay: e.target.value })} className={inputCls}>
                                        {DAYS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Distrito</label>
                                    <select value={client.district} onChange={(e) => setClient({ ...client, district: e.target.value })} className={inputCls}>
                                        <option value="040129">JOSE LUIS BUSTAMANTE Y RIVERO</option>
                                        <option value="040119">SOCABAYA</option>
                                        <option value="040113">SABANDIA</option>
                                        <option value="040104">CHARACATO</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className={labelCls}>Dirección actual</label>
                                    <input type="text" maxLength={500} value={client.address || ""} onChange={(e) => setClient({ ...client, address: e.target.value })} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Latitud</label>
                                    <input type="text" readOnly value={client.latitude} className={`${inputCls} bg-gray-50 dark:bg-gray-600 cursor-not-allowed`} />
                                </div>
                                <div>
                                    <label className={labelCls}>Longitud</label>
                                    <input type="text" readOnly value={client.longitude} className={`${inputCls} bg-gray-50 dark:bg-gray-600 cursor-not-allowed`} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className={labelCls}>Comentarios</label>
                                    <textarea rows={3} maxLength={500} value={client.comment || ""} onChange={(e) => setClient({ ...client, comment: e.target.value })} className={`${inputCls} resize-none`} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={client.isEnabled} onChange={(e) => setClient({ ...client, isEnabled: e.target.checked })} className="sr-only" />
                                        <span className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${client.isEnabled ? "bg-emerald-600" : "bg-gray-300 dark:bg-gray-600"}`}>
                                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${client.isEnabled ? "translate-x-5" : "translate-x-0"}`} />
                                        </span>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Activo</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                            <button type="button" onClick={() => setIsModalOpen(false)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors">
                                <i className="fas fa-times"></i>Cerrar
                            </button>
                            <button type="button" onClick={handleSaveClient} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 transition-colors">
                                <i className="fas fa-save"></i>Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
